import 'react-simple-keyboard/build/css/index.css';
import './WebKeyboard.css';

import React, { useEffect, useRef, useState } from 'react';
import { findNodeHandle, StyleSheet, TextInput, View } from 'react-native';
import Keyboard from 'react-simple-keyboard';
import keyNavigation from 'simple-keyboard-key-navigation';

import { Theme } from '@constants';

import { isTizen, isVizio, isX1 } from '../../../../helpers/Platform';
import { FOCUS_NOW } from '../../../../helpers/Tools';
import { FocusableHighlight } from '../../../focusable/FocusableHighlight/FocusableHighlight';

export const WebKeyboard = ({
  onKeyPressed,
  onTextChanged,
  onSubmit,
  returnKeyLabel,
  defaultValue,
  useMiniKeyboard = false,
  offsetX = 0,
  keyboardWidth = 1920,
  hideKeyboardInput = false,
  wrapHorizontal = true,
  wrapVertical = true,
  backgroundColor,
  dismissOnLeft = false,
  dismissOnRight = false,
  dismissOnUp = false,
  numericOnly = false,
}) => {
  const keyboardReference = useRef(null);
  const keyboardLayoutReference = useRef('default');
  const focusReference = useRef(null);
  const [input, setInput] = useState(defaultValue ?? '');

  const onChange = text => {
    setInput(text);
    onTextChanged && onTextChanged(text);
  };

  const handleEnter = () => {
    onSubmit && onSubmit();
  };

  const onKeyPress = key => {
    if (key === '{enter}') {
      handleEnter();
    } else if (onKeyPressed) {
      onKeyPressed(key);
    }

    /**
     * Handle toggles
     */
    if (key.includes('{') && key.includes('}')) {
      handleLayoutChange(key);
    }
  };

  const handleLayoutChange = key => {
    const currentLayout = keyboardReference.current.options.layoutName;
    let layoutName;

    switch (key) {
      case '{shift}':
      case '{shiftactivated}':
      case '{default}':
        layoutName = currentLayout === 'default' ? 'shift' : 'default';
        break;

      case '{alt}':
      case '{altright}':
        layoutName = currentLayout === 'alt' ? 'default' : 'alt';
        break;

      default:
        break;
    }

    if (layoutName) {
      keyboardLayoutReference.current = layoutName;
      keyboardReference.current.setOptions({
        layoutName,
      });

      const navigation = keyboardReference.current?.modules?.keyNavigation;
      const focusNode = focusReference.current;
      // Update general focus area with accessibility label for focused key

      if (navigation?.markedBtn?.ariaLabel && focusNode) {
        console.log(navigation.markedBtn);
        const node = findNodeHandle(focusReference.current);

        if (node) {
          node.ariaLabel = navigation.markedBtn.ariaLabel;
        }
      }
    }
  };

  const keyLayout = {
    default: numericOnly
      ? ['1 2 3', '4 5 6', '7 8 9', '0 {bksp} {enter}']
      : [
          'a b c d e f g h i j k l m',
          'n o p q r s t u v w x y z',
          '1 2 3 4 5 6 7 8 9 0 - . ,',
          '{shift} {alt} {space} {bksp} {enter}',
        ],
    shift: [
      'A B C D E F G H I J K L M',
      'N O P Q R S T U V W X Y Z',
      '1 2 3 4 5 6 7 8 9 0 - . ,',
      '{shift} {alt} {space} {bksp} {enter}',
    ],
    alt: [
      '/ : ; ( ) [ ] $ & @ " ^',
      "? ! ' # + = * _ < > { } ~",
      '1 2 3 4 5 6 7 8 9 0 - . ,',
      '{default} {space} {bksp} {enter}',
    ],
  };

  const miniKeyLayout = {
    default: numericOnly
      ? ['1 2 3', '4 5 6', '7 8 9', '0 {bksp} {enter}']
      : [
          'a b c d e f',
          'g h i j k l',
          'm n o p q r',
          's t u v w x',
          'y z 1 2 3 4',
          '5 6 7 8 9 0',
          '{shift} {alt} {space} {bksp} {enter}',
        ],
    shift: [
      'A B C D E F',
      'G H I J K L',
      'M N O P Q R',
      'S T U V W X',
      'Y Z 1 2 3 4',
      '5 6 7 8 9 0',
      '{shift} {alt} {space} {bksp} {enter}',
    ],
    alt: ['- / : ; ( )', '$ & @ " . ,', "? ! ' # + =", '* _ < > { }', '{default} {space} {bksp} {enter}'],
  };

  const numberCols = row => {
    const currentLayout = keyboardLayoutReference.current;

    return useMiniKeyboard
      ? miniKeyLayout[currentLayout][row].split(' ').length
      : keyLayout[currentLayout][row].split(' ').length;
  };

  const numberRows = () => {
    const currentLayout = keyboardLayoutReference.current;

    return useMiniKeyboard ? miniKeyLayout[currentLayout].length : keyLayout[currentLayout].length;
  };

  const keyboardNavigationHandler = keyEvent => {
    const navigation = keyboardReference.current.modules.keyNavigation;
    const code = keyEvent.keyCode;
    // Handle a-z, A-Z, and 0-9 keycodes by allowing input directly

    if ((code >= 65 && code <= 90) || (code >= 48 && code <= 57) || (code >= 188 && code <= 192)) {
      keyboardReference.current.setInput(`${input}${keyEvent.key}`);
      onChange(`${input}${keyEvent.key}`);
      return;
      // Handle custom back button keycodes for x1 and samsung
    } else if (code === 8 || code === 10009) {
      keyEvent.stopImmediatePropagation();
      keyEvent.preventDefault();
      handleEnter();
      return;
    }

    switch (keyEvent.key) {
      case 'ArrowUp': {
        if (navigation.lastMarkerPos && numberRows() > 1) {
          const [currentRow, currentCol] = navigation.lastMarkerPos;
          const numberOfColumns = numberCols(currentRow);
          const numberOfRows = numberRows();

          if (currentRow === 0 && dismissOnUp) {
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            handleEnter();
            return;
          }

          const nextRow = currentRow === 0 ? numberOfRows - 1 : currentRow - 1;
          const numberOfColumnsNext = numberCols(nextRow);

          if (currentRow === 0 && !wrapVertical) {
            navigation.up();
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          } else {
            const mult = numberOfColumnsNext / numberOfColumns;
            const nextCol = Math.floor(currentCol * mult);

            navigation.setMarker(nextRow, nextCol);
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          }
        }
        navigation.up();
        keyEvent.stopImmediatePropagation();
        keyEvent.preventDefault();
        break;
      }
      case 'ArrowDown': {
        if (navigation.lastMarkerPos && numberRows() > 1) {
          const [currentRow, currentCol] = navigation.lastMarkerPos;
          const numberOfColumns = numberCols(currentRow);
          const numberOfRows = numberRows();

          const nextRow = currentRow === numberOfRows - 1 ? 0 : currentRow + 1;
          const numberOfColumnsNext = numberCols(nextRow);

          if (currentRow === numberOfRows - 1 && !wrapVertical) {
            navigation.down();
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          } else {
            const mult = numberOfColumnsNext / numberOfColumns;
            const nextCol = Math.floor(currentCol * mult);

            navigation.setMarker(nextRow, nextCol);
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          }
        }
        navigation.down();
        keyEvent.stopImmediatePropagation();
        keyEvent.preventDefault();
        break;
      }
      case 'ArrowLeft': {
        if (navigation.lastMarkerPos) {
          const [currentRow, currentCol] = navigation.lastMarkerPos;
          const numberOfColumns = numberCols(currentRow);

          if (currentCol === 0 && dismissOnLeft) {
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            handleEnter();
            return;
          }

          if (currentCol === 0 && wrapHorizontal) {
            // Wrap around
            navigation.setMarker(currentRow, numberOfColumns - 1);
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          }
        }
        navigation.left();
        keyEvent.stopImmediatePropagation();
        keyEvent.preventDefault();
        break;
      }
      case 'ArrowRight': {
        if (navigation.lastMarkerPos) {
          const [currentRow, currentCol] = navigation.lastMarkerPos;
          const numberOfColumns = numberCols(currentRow);

          if (currentCol === numberOfColumns - 1 && dismissOnRight) {
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            handleEnter();
            return;
          }

          if (currentCol === numberOfColumns - 1 && wrapHorizontal) {
            // Wrap around
            navigation.setMarker(currentRow, 0);
            keyEvent.stopImmediatePropagation();
            keyEvent.preventDefault();
            break;
          }
        }
        navigation.right();
        keyEvent.stopImmediatePropagation();
        keyEvent.preventDefault();
        break;
      }
      case 'Backspace': // fallthough
      case 'Escape': {
        keyEvent.stopImmediatePropagation();
        keyEvent.preventDefault();
        handleEnter();
        break;
      }
    }

    // Update general focus area with accessibility label for focused key
    if (navigation.markedBtn.ariaLabel && focusReference.current) {
      const node = findNodeHandle(focusReference.current);

      if (node) {
        node.ariaLabel = navigation.markedBtn.ariaLabel;
      }
    }
  };

  class keyboardAccessibilityModule {
    init = keyboard => {
      keyboard.registerModule('keyboardAccessibility', module => {
        module.fn = {};
        module.fn.onRender = keyboard.onRender;

        module.keyMap = {
          '{bksp}': 'delete',
          '{enter}': returnKeyLabel,
          '{space}': 'space',
          '{shift}': 'shift. Select to toggle.',
          '{shiftactivated}': 'shift. Pressed. Select to toggle.',
          '{alt}': 'Alternate keys.',
          '{default}': 'Change keyboard.',
          '-': 'dash',
          '.': 'period',
          ',': 'comma',
          '/': 'forward slash',
          '\\': 'back slash',
          ':': 'colon',
          ';': 'semi colon',
          '(': 'left parenthesis',
          ')': 'right parenthesis',
          '[': 'open square bracket',
          ']': 'close square bracket',
          $: 'dollar sign',
          '&': 'ampersand',
          '@': 'at sign',
          '"': 'double quote',
          '^': 'caret',
          '?': 'question mark',
          '!': 'exclamation mark',
          "'": 'single quote',
          '#': 'hash',
          '+': 'plus',
          '=': 'equals',
          '*': 'asterisk',
          _: 'underscore',
          '<': 'open angle bracket',
          '>': 'close angle bracket',
          '{': 'open curly bracket',
          '}': 'close curly bracket',
          '~': 'tilde',
        };

        keyboard.onRender = () => {
          module.fn.onRender();
          for (const key in keyboard.buttonElements) {
            const keyDivs = keyboard.buttonElements[key];
            //keyDiv.setAttribute(`aria-label`, key);

            if (Array.isArray(keyDivs) && keyDivs.length > 0) {
              const keyDiv = keyDivs[0];

              keyDiv.ariaLabel = module.keyMap[key] ?? key;
            }
          }
        };
      });
    };
  }

  useEffect(() => {
    document.addEventListener('keydown', keyboardNavigationHandler, true);
    FOCUS_NOW(focusReference);
    return () => {
      document.removeEventListener('keydown', keyboardNavigationHandler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardReference.current, onSubmit, input]);

  useEffect(() => {
    if (defaultValue && keyboardReference.current && keyboardReference.current.input?.default === '') {
      keyboardReference.current.setInput(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardReference.current, defaultValue]);

  return (
    <>
      <View
        style={[
          styles.keyboardBackground,
          useMiniKeyboard ? styles.miniKeyboardBackground : null,
          { transform: [{ translateX: offsetX }] },
        ]}
      >
        <View
          style={[
            styles.keyboardWrapper,
            { width: Theme.px(keyboardWidth) },
            useMiniKeyboard ? { backgroundColor: Theme.transparent } : null,
          ]}
        >
          <View
            style={
              useMiniKeyboard
                ? [
                    {
                      backgroundColor,
                      marginTop: hideKeyboardInput ? Theme.px(120) : 0,
                      width: (Theme.px(keyboardWidth) / 100) * 75,
                    },
                    styles.miniKeyWrapper,
                  ]
                : null
            }
          >
            {hideKeyboardInput ? null : (
              <View style={[styles.keyboardInput, backgroundColor ? { backgroundColor } : null]}>
                <TextInput style={[styles.textInput, { width: (Theme.px(keyboardWidth) * 2) / 3 }]} value={input} />
              </View>
            )}
            {!useMiniKeyboard && hideKeyboardInput ? (
              <View
                style={{
                  width: (Theme.px(keyboardWidth) * 2) / 3,
                  height: Theme.px(30),
                }}
              />
            ) : null}
            <Keyboard
              display={{
                '{bksp}': isX1 || isTizen || isVizio ? 'Del' : '⌫', // Using `Del` only for X1/Tizen because it doesn't seem to render ⌫ correctly.
                '{enter}': returnKeyLabel,
                '{space}': '________',
                '{shift}': '⇧',
                '{shiftactivated}': '⇧',
                '{alt}': '!#$',
                '{default}': 'ABC',
              }}
              enableKeyNavigation
              keyboardRef={reference => (keyboardReference.current = reference)}
              layout={useMiniKeyboard ? miniKeyLayout : keyLayout}
              layoutName={keyboardLayoutReference.current ?? 'default'}
              modules={[keyNavigation, keyboardAccessibilityModule]}
              testID={'web_virtual_keyboard'}
              onChange={onChange}
              onKeyPress={onKeyPress}
            />
          </View>
        </View>
      </View>
      <FocusableHighlight
        nextFocusDown={null}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={null}
        ref={focusReference}
        style={styles.focusTrap}
        testID={'web_virtual_keyboard_focus_trap'}
        onPress={event => {
          keyboardReference.current.modules.keyNavigation.press();
        }}
      >
        <View />
      </FocusableHighlight>
    </>
  );
};

const styles = StyleSheet.create({
  focusTrap: {
    height: 0,
    width: 0,
  },
  keyboardBackground: {
    alignItems: 'center',
    height: Theme.px(1080),
    justifyContent: 'flex-end',
    left: 0,
    position: 'fixed',
    top: 0,
    width: Theme.px(1920),
    zIndex: 10000,
  },
  keyboardInput: {
    alignItems: 'center',
    backgroundColor: Theme.blackPearlBlue,
    height: Theme.px(120),
    justifyContent: 'center',
    width: '100%',
  },
  keyboardWrapper: {
    alignContent: 'center',
    alignItems: 'center',
    backgroundColor: Theme.blackPearlBlue,
    justifyContent: 'center',
    paddingBottom: Theme.tvMarginVertical(),
    paddingLeft: Theme.tvMarginHorizontal(),
    paddingRight: Theme.tvMarginHorizontal(),
    width: Theme.px(1920),
  },
  miniKeyWrapper: {
    borderRadius: Theme.tileBorderRadius,
    paddingVertical: Theme.px(5),
  },
  miniKeyboardBackground: {
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  textInput: {
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.2),
    borderColor: Theme.whisperWhite,
    borderWidth: Theme.px(4),
    color: Theme.whisperWhite,
    fontFamily: 'Roboto-Light',
    fontSize: Theme.px(32),
    height: Theme.px(90),
    marginTop: Theme.px(5),
    minWidth: Theme.px(400),
    padding: Theme.px(23),
    width: Theme.px(1280),
  },
});
